import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';

const initialState = {};

// Constants
const AUTHORIZATION_FAILED_MESSAGE = 'Authorization failed';

export const driverSliceV2 = createSlice({
	name: 'driverStateV2',
	initialState,
	reducers: {},
});

/**
 * Create Driver V2
 * @param {Object} req_data - Driver data
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 *
 * Request structure:
 * {
 *   vendor_id: 5 (required, integer, must exist in vendors_v2),
 *   vehicle_type_id: 1 (required, integer, must exist in vehicle_types_v2),
 *   driver_name: "Venkatesh Babu" (required, string, max 255 chars),
 *   mobile_number: "9876543234" (required, string, max 20 chars, valid phone),
 *   document_info: {
 *     driving_license: {
 *       document_key: "vehicle/..." (required, string - S3 key),
 *       valid_till: "2028-12-12" (required, date YYYY-MM-DD),
 *       number: "DL1234567890" (required, string, max 50 chars)
 *     },
 *     aadhar: {
 *       document_key: "vehicle/..." (required, string - S3 key),
 *       number: "5600 2222 2023" (required, string, max 20 chars, format: "XXXX XXXX XXXX")
 *     }
 *   }
 * }
 *
 * Error codes:
 * - VALIDATION_ERROR: Missing required fields or invalid data types
 * - DEPENDENCY_ERROR: Invalid vendor_id or vehicle_type_id
 * - DUPLICATE_MOBILE_NUMBER: Mobile number already assigned to another driver
 * - DUPLICATE_GPS_DEVICE: GPS device ID already assigned to another driver
 */
export const createDriverV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			create_type: 'driver',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Driver created successfully');
			}
			return res;
		});
	};

/**
 * Update Driver V2
 * Endpoint: POST /api/v2/update
 * @param {Object} req_data - Driver update data
 *   - id: Integer (required) - Driver ID
 *   - vehicle_type_id: Integer (optional)
 *   - driver_name: String (optional, max 255 chars)
 *   - mobile_number: String (optional, max 20 chars)
 *   - gps_device_id: String (optional, must be unique if changed)
 *   - document_info: Object (optional)
 *     - driving_license: Object
 *       - document_key: String (S3 key)
 *       - valid_till: Date (YYYY-MM-DD)
 *       - number: String (max 50 chars)
 *     - aadhar: Object (optional)
 *       - document_key: String (S3 key)
 *       - number: String (max 20 chars)
 *   - kyc_status: String (optional) - Enum: ["pending", "verified", "rejected"]
 *   - certifications: Object (optional)
 *   - availability_status: String (optional) - Enum: ["available", "busy", "offline"]
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 *
 * Response (Success - 200):
 * {
 *   "status": "success",
 *   "data": {
 *     "id": 3,
 *     "vendor_id": 5,
 *     "vendor_name": "XYZ Logistics",
 *     "vehicle_type_id": 2,
 *     "vehicle_type_name": "Mahindra Bolero",
 *     "driver_name": "Venkatesh Babu Updated",
 *     "mobile_number": "9876543235",
 *     "gps_device_id": "GPS-DRIVER-002",
 *     "kyc_status": "verified",
 *     "availability_status": "available",
 *     "updated_at": "2024-01-16T14:30:00Z",
 *     "updated_by": 10
 *   },
 *   "message": "Driver updated successfully"
 * }
 */
export const updateDriverV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			update_type: 'driver',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS)
			.then((res) => {
				if (!res) {
					return { status: 'error', message: AUTHORIZATION_FAILED_MESSAGE };
				}
				if (res.status === 'success') {
					toaster.success(res.message || 'Driver updated successfully');
				} else {
					const errorMessage =
						res?.error?.message || res?.message || 'Failed to update driver';
					toaster.error(errorMessage);
				}
				return res;
			})
			.catch((error) => {
				const errorMessage =
					error?.response?.data?.error?.message ||
					error?.response?.data?.message ||
					error?.message ||
					'Failed to update driver. Please try again.';
				toaster.error(errorMessage);
				return Promise.reject(error);
			});
	};

/**
 * Transfer Driver V2
 * Endpoint: POST /api/v2/update
 * @param {Object} req_data - Transfer data
 *   - driver_id: Integer (required) - Must exist in drivers_v2 table
 *   - to_vendor_id: Integer (required) - Must exist in vendors_v2 table, must be different from current vendor_id
 *   - reason: String (required, max 255 chars)
 *   - last_working_date: ISO Date String (required)
 *   - start_date: ISO Date String (required, must be >= last_working_date)
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 *
 * Request structure:
 * {
 *   "update_type": "transfer_driver",
 *   "data": {
 *     "driver_id": 10,
 *     "to_vendor_id": 5,
 *     "reason": "Vendor change request",
 *     "last_working_date": "2024-01-15T00:00:00Z",
 *     "start_date": "2024-01-16T00:00:00Z"
 *   }
 * }
 *
 * Response (Success - 200):
 * {
 *   "status": "success",
 *   "data": {
 *     "driver_id": 10,
 *     "driver_name": "John Doe",
 *     "from_vendor_id": 3,
 *     "from_vendor_name": "Old Vendor",
 *     "to_vendor_id": 5,
 *     "to_vendor_name": "XYZ Logistics",
 *     "approval_id": 456,
 *     "approval_status": "pending",
 *     "reason": "Vendor change request"
 *   },
 *   "message": "Driver transfer request created. Transfer will be executed after approval."
 * }
 *
 * Business Rules:
 * - Driver must not have active trips (status: "planned" or "in_progress")
 * - Driver must not have pending assignments
 * - Creates a pending approval entry in approvals_v2 table with approval_type='driver_transfer'
 * - Transfer will be executed only after approval via handle_driver_transfer_approval API
 * - Transfer history will be created in driver_transfer_history_v2 table only after approval
 *
 * Error codes:
 * - NOT_FOUND: Driver or vendor not found
 * - INVALID_STATE: Driver has active trips or pending assignments
 * - SAME_VENDOR: to_vendor_id same as current vendor_id
 * - VALIDATION_ERROR: Invalid dates or missing required fields
 */
export const transferDriverV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			update_type: 'transfer_driver',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS)
			.then((res) => {
				if (res.status === 'success') {
					toaster.success(
						res.message ||
							'Driver transfer request created. Transfer will be executed after approval.'
					);
					return res;
				}
				// Handle error responses
				if (res.status === 'error' && res.error) {
					const errorCode = res.error.code;
					const errorMessage = res.error.message;

					switch (errorCode) {
						case 'INVALID_STATE':
							toaster.error(
								errorMessage ||
									'Driver has active trips or pending assignments. Cannot transfer.'
							);
							break;
						case 'SAME_VENDOR':
							toaster.error(
								errorMessage ||
									'Cannot transfer to the same vendor. Please select a different vendor.'
							);
							break;
						case 'NOT_FOUND':
							toaster.error(errorMessage || 'Driver or vendor not found.');
							break;
						case 'VALIDATION_ERROR':
							toaster.error(
								errorMessage || 'Invalid input. Please check all fields.'
							);
							break;
						default:
							toaster.error(errorMessage || 'Failed to transfer driver.');
					}
				}
				return res;
			})
			.catch((error) => {
				// Handle network errors or other exceptions
				const errorMessage =
					error?.response?.data?.error?.message ||
					error?.message ||
					'Failed to transfer driver. Please try again.';
				toaster.error(errorMessage);
				return Promise.reject(error);
			});
	};

/**
 * Update Driver Status V2 (Toggle Active/Inactive)
 * @param {Number} id - Driver ID
 * @param {Boolean} is_active - Active status
 * @returns {Promise} API response
 */
export const updateDriverStatusV2 =
	({ id, is_active }) =>
	async () => {
		const data = {
			update_type: 'driver',
			data: {
				id,
				is_active,
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(
					res.message || `Driver ${is_active ? 'activated' : 'deactivated'} successfully`
				);
			}
			return res;
		});
	};

/**
 * Clean filter object by removing undefined/null/empty values
 * @param {Object} filter_by - Filter object to clean
 * @returns {Object} Cleaned filter object
 */
const cleanFilterBy = (filter_by) => {
	const cleaned = {};
	if (!filter_by) return cleaned;

	Object.keys(filter_by).forEach((key) => {
		const value = filter_by[key];
		if (value !== undefined && value !== null && value !== '') {
			cleaned[key] = value;
		}
	});
	return cleaned;
};

/**
 * Transform driver data to match table format
 * @param {Object} driver - Driver object from API
 * @returns {Object} Transformed driver object
 */
const transformDriver = (driver) => ({
	id: driver.id,
	driver_name: driver.driver_name,
	vendor_name: driver.vendor_name || '-',
	mobile_no: driver.mobile_number,
	city: driver.city || '-',
	dl_proof: driver.document_info?.driving_license?.valid_till || null,
	multiple_trips: driver.multiple_trips || false,
	created_at: driver.created_at,
	created_by_name: driver.created_by_name || '-',
	is_active: driver.is_active,
	vendor_id: driver.vendor_id,
	vehicle_type_ids: driver.vehicle_type_ids || driver.vehicle_type_id,
	vehicle_type_name: driver.vehicle_type_name,
	...driver,
});

/**
 * Build request data for driver search
 * @param {Object} params - Search parameters
 * @returns {Object} Request data object
 */
const buildSearchRequest = ({ cleanedFilters, offset, limit, sort_column, sort_direction }) => {
	const requestData = {
		search_type: 'driver',
		filter_by: cleanedFilters,
		pagination: {
			page: Math.floor(offset / limit) + 1,
			per_page: limit,
		},
	};

	if (sort_column && sort_direction) {
		requestData.sort_by = {
			field: sort_column,
			order: sort_direction,
		};
	}

	return requestData;
};

/**
 * Process API response and transform data
 * @param {Object} response - API response
 * @returns {Object} Transformed response with rows, count, cities, vendors
 */
const processSearchResponse = (response) => {
	const drivers = Array.isArray(response?.data?.drivers) ? response.data.drivers : [];
	const paginationData = response?.data?.pagination || {};

	return {
		rows: drivers.map(transformDriver),
		count: paginationData.total || drivers.length || 0,
		cities: response?.data?.filter_list?.cities || [],
		vendors: response?.data?.filter_list?.vendors || [],
	};
};

/**
 * Search Drivers V2
 * Endpoint: POST /api/v2/search
 * @param {Object} filter_by - Filter criteria
 * @param {Number} offset - Pagination offset
 * @param {Number} limit - Pagination limit
 * @param {String} sort_column - Sort column name
 * @param {String} sort_direction - Sort direction (ASC/DESC)
 * @returns {Promise} API response with drivers array and count
 *
 * Filter options:
 * - driver_name: String (partial match)
 * - vendor_id: Integer
 * - vehicle_type_id: Integer
 * - mobile_number: String
 * - city: String
 * - kyc_status: String - Enum: ["pending", "verified", "rejected"]
 * - availability_status: String - Enum: ["available", "busy", "offline"]
 * - gps_device_id: String
 * - is_active: Boolean
 * - created_from: Date (ISO string) - Filter drivers created after this date
 * - created_to: Date (ISO string) - Filter drivers created before this date
 * - created_at_from: Date (ISO string) - Alias for created_from (mapped automatically)
 * - created_at_to: Date (ISO string) - Alias for created_to (mapped automatically)
 * - created_by: Integer
 *
 * Request structure:
 * {
 *   "search_type": "driver",
 *   "filter_by": { ... },
 *   "pagination": { "page": 1, "per_page": 20 },
 *   "sort_by": { "field": "created_at", "order": "desc" }
 * }
 *
 * Response structure:
 * {
 *   "status": "success",
 *   "data": {
 *     "drivers": [...],
 *     "pagination": { "page": 1, "per_page": 20, "total": 45, "total_pages": 3 }
 *   }
 * }
 */
export const searchDriversV2 =
	({ filter_by = {}, offset = 0, limit = 20, sort_column, sort_direction }) =>
	async () => {
		const cleanedFilters = cleanFilterBy(filter_by);
		const requestData = buildSearchRequest({
			cleanedFilters,
			offset,
			limit,
			sort_column,
			sort_direction,
		});

		const CREDENTIALS = {
			url: '/search',
			data: requestData,
			version: 'v2',
		};

		return API.common(CREDENTIALS)
			.then((response) => {
				// Check if response exists (ACL validation might return undefined)
				if (!response) {
					return {
						rows: [],
						count: 0,
						cities: [],
						vendors: [],
					};
				}
				return processSearchResponse(response);
			})
			.catch((error) => {
				console.error('Search drivers error:', error);
				// Return empty data structure on error
				return {
					rows: [],
					count: 0,
					cities: [],
					vendors: [],
				};
			});
	};

/**
 * Get Driver by ID V2
 * @param {Number} id - Driver ID
 * @returns {Promise} Driver object or null
 */
export const getDriverByIdV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'driver',
				filter_by: {
					id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// API returns { status: "success", data: { driver: {...} } }
			if (response?.status === 'success' && response?.data?.driver) {
				return response.data.driver;
			}
			if (response?.status === 'success' && Array.isArray(response?.data?.drivers)) {
				return response.data.drivers[0] || null;
			}
			return null;
		});
	};

/**
 * Export Drivers CSV V2
 * @param {Object} filter_by - Filter criteria for export
 * @param {String} format - Export format (default: 'csv')
 * @returns {Promise} API response with download_url
 */
export const exportDriversCSVV2 =
	({ filter_by = {}, format = 'csv' }) =>
	async () => {
		const cleanedFilters = cleanFilterBy(filter_by);

		const CREDENTIALS = {
			url: '/report',
			data: {
				report_type: 'driver_export',
				format: format || 'csv',
				data: {
					filter_by: cleanedFilters,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success' && response?.data?.download_url) {
				// Open download URL in new tab
				window.open(response.data.download_url, '_blank');
				toaster.success(
					`Export generated successfully. ${
						response.data.total_records || 0
					} records exported.`
				);
			}
			return response;
		});
	};

/**
 * Sync Drivers V2 (from external system or database)
 * @returns {Promise} API response
 */
export const syncDriversV2 = () => async () => {
	const CREDENTIALS = {
		url: '/sync',
		data: {
			sync_type: 'driver',
		},
		version: 'v2',
	};

	return API.common(CREDENTIALS)
		.then((res) => {
			// Check if response exists (ACL validation might return undefined)
			if (!res) {
				return { status: 'error', message: AUTHORIZATION_FAILED_MESSAGE };
			}

			if (res.status === 'success') {
				toaster.success(res.message || 'Drivers synced successfully');
			} else {
				// Handle error response from API
				const errorMessage =
					res?.error?.message || res?.message || 'Failed to sync drivers';
				toaster.error(errorMessage);
			}
			return res;
		})
		.catch((error) => {
			// Handle network errors or other exceptions
			const errorMessage =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				'Failed to sync drivers. Please try again.';
			toaster.error(errorMessage);
			return Promise.reject(error);
		});
};

/**
 * Search Driver Filters V2 (for dropdowns/autocomplete)
 * Endpoint: POST /api/v2/list_search
 * @param {Object} filter_by - Filter criteria
 *   - search_term (string, optional): Searches in driver_name, mobile_number
 *   - vendor_id (integer, optional)
 *   - kyc_status (string, optional): Enum ["pending", "verified", "rejected"]
 *   - availability_status (string, optional): Enum ["available", "busy", "offline"]
 *   - is_active (boolean, optional)
 * @param {Number} limit - Maximum number of results (default: 50, min: 1, max: 100)
 * @returns {Promise} Object with { results: [{ id, label, value, metadata }], total }
 *
 * Response format:
 * {
 *   results: [
 *     {
 *       id: 3,
 *       label: "Venkatesh Babu - XYZ Logistics",
 *       value: 3,
 *       metadata: {
 *         vendor_name: "XYZ Logistics",
 *         vehicle_type_id: 1,
 *         vehicle_type_name: "Tata Ace",
 *         mobile_number: "9876543234",
 *         kyc_status: "verified",
 *         availability_status: "available"
 *       }
 *     }
 *   ],
 *   total: 1
 * }
 */
export const searchDriverFiltersV2 =
	({ filter_by = {}, limit = 50 }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanedFilters = cleanFilterBy(filter_by);

		// Validate and clamp limit
		const validatedLimit = Math.min(Math.max(limit || 50, 1), 1000);

		const CREDENTIALS = {
			url: '/list_search',
			data: {
				list_search_type: 'driver',
				filter_by: cleanedFilters,
				limit: validatedLimit,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS)
			.then((response) => {
				// Check if response exists (ACL validation might return undefined)
				if (!response) {
					return {
						results: [],
						total: 0,
					};
				}
				return {
					results: response?.data?.results || [],
					total: response?.data?.total || 0,
				};
			})
			.catch((error) => {
				console.error('Search driver filters error:', error);
				return {
					results: [],
					total: 0,
				};
			});
	};

/**
 * Get Driver Profile V2
 * Endpoint: POST /api/v2/search
 * @param {Number} driver_id - Driver ID
 * @returns {Promise} API response with driver profile, statistics, recent trips, and transfer history
 *
 * Request structure:
 * {
 *   "search_type": "driver_profile",
 *   "filter_by": {
 *     "driver_id": 3
 *   }
 * }
 *
 * Response structure:
 * {
 *   "status": "success",
 *   "data": {
 *     "driver": { ... },
 *     "statistics": {
 *       "total_trips": 150,
 *       "completed_trips": 145,
 *       "active_trips": 2,
 *       "cancelled_trips": 3,
 *       "total_distance_km": 15000.5,
 *       "average_rating": 4.5
 *     },
 *     "recent_trips": [...],
 *     "transfer_history": [...]
 *   }
 * }
 */
export const getDriverProfileV2 =
	({ driver_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'driver_profile',
				filter_by: {
					driver_id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS)
			.then((response) => {
				// Check if response exists (ACL validation might return undefined)
				if (!response) {
					return {
						driver: null,
						statistics: null,
						recent_trips: [],
						transfer_history: [],
					};
				}

				if (response?.status === 'success' && response?.data) {
					return {
						driver: response.data.driver || null,
						statistics: response.data.statistics || null,
						recent_trips: response.data.recent_trips || [],
						transfer_history: response.data.transfer_history || [],
					};
				}

				return {
					driver: null,
					statistics: null,
					recent_trips: [],
					transfer_history: [],
				};
			})
			.catch((error) => {
				console.error('Get driver profile error:', error);
				const errorMessage =
					error?.response?.data?.error?.message ||
					error?.response?.data?.message ||
					error?.message ||
					'Failed to fetch driver profile';
				toaster.error(errorMessage);
				return {
					driver: null,
					statistics: null,
					recent_trips: [],
					transfer_history: [],
				};
			});
	};

/**
 * Replace Driver V2
 * Endpoint: POST /api/v2/update
 * @param {Object} req_data - Replace driver data
 *   - old_driver_id: Integer (required) - Must exist in drivers_v2 table
 *   - new_driver_id: Integer (required) - Must exist in drivers_v2 table, must be different from old_driver_id
 *   - trip_ids: Array of Integers (required) - All trip_ids must exist in trips_v2 table, all trips must belong to old_driver_id
 *   - reason: String (required, max 255 chars)
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 *
 * Request structure:
 * {
 *   "update_type": "replace_driver",
 *   "data": {
 *     "old_driver_id": 1,
 *     "new_driver_id": 2,
 *     "trip_ids": [10, 11],
 *     "reason": "Driver unavailable"
 *   }
 * }
 *
 * Response (Success - 200):
 * {
 *   "status": "success",
 *   "data": {
 *     "old_driver_id": 1,
 *     "new_driver_id": 2,
 *     "trips_replaced": 2,
 *     "trip_ids": [10, 11],
 *     "reason": "Driver unavailable"
 *   },
 *   "message": "Driver replaced successfully for 2 trips"
 * }
 *
 * Error codes:
 * - NOT_FOUND: Driver or trip not found
 * - VALIDATION_ERROR: Invalid input or missing required fields
 * - INVALID_STATE: Trips don't belong to old_driver_id or new driver not available
 */

// Helper: Handle successful driver replacement
const handleReplaceDriverSuccess = (res) => {
	toaster.success(
		res.message || `Driver replaced successfully for ${res.data?.trips_replaced || 0} trips`
	);
};

// Helper: Handle driver replacement errors
const handleReplaceDriverError = (res) => {
	if (res.status === 'error' && res.error) {
		const errorCode = res.error.code;
		const errorMessage = res.error.message;

		switch (errorCode) {
			case 'NOT_FOUND':
				toaster.error(errorMessage || 'Driver or trip not found.');
				break;
			case 'VALIDATION_ERROR':
				toaster.error(errorMessage || 'Invalid input. Please check all fields.');
				break;
			case 'INVALID_STATE':
				toaster.error(
					errorMessage ||
						'Trips do not belong to the old driver or new driver is not available.'
				);
				break;
			default:
				toaster.error(errorMessage || 'Failed to replace driver.');
		}
		return;
	}

	const errorMessage = res?.error?.message || res?.message || 'Failed to replace driver';
	toaster.error(errorMessage);
};

export const replaceDriverV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			update_type: 'replace_driver',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS)
			.then((res) => {
				if (!res) {
					return { status: 'error', message: AUTHORIZATION_FAILED_MESSAGE };
				}

				if (res.status === 'success') {
					handleReplaceDriverSuccess(res);
					return res;
				}

				handleReplaceDriverError(res);
				return res;
			})
			.catch((error) => {
				// Handle network errors or other exceptions
				const errorMessage =
					error?.response?.data?.error?.message ||
					error?.response?.data?.message ||
					error?.message ||
					'Failed to replace driver. Please try again.';
				toaster.error(errorMessage);
				return Promise.reject(error);
			});
	};

/**
 * Get Driver Transfer History V2
 * Endpoint: POST /api/v2/search
 * @param {Number} driver_id - Driver ID
 * @returns {Promise} API response with driver transfer history
 *
 * Request structure:
 * {
 *   "search_type": "driver_transfer_history",
 *   "filter_by": {
 *     "driver_id": 10
 *   }
 * }
 *
 * Response structure:
 * {
 *   "status": "success",
 *   "data": {
 *     "driver_id": 10,
 *     "driver_name": "Venkatesh Babu",
 *     "current_vendor_id": 5,
 *     "current_vendor_name": "XYZ Logistics",
 *     "transfer_history": [
 *       {
 *         "transfer_id": 123,
 *         "from_vendor_id": 3,
 *         "from_vendor_name": "Old Vendor",
 *         "to_vendor_id": 5,
 *         "to_vendor_name": "XYZ Logistics",
 *         "reason": "Vendor change request",
 *         "last_working_date": "2024-01-15T00:00:00Z",
 *         "start_date": "2024-01-16T00:00:00Z",
 *         "transferred_by": 10,
 *         "transferred_by_name": "Admin User",
 *         "transferred_at": "2024-01-16T10:30:00Z"
 *       }
 *     ],
 *     "total_transfers": 2
 *   }
 * }
 *
 * Implementation Notes:
 * - Returns chronological list (newest first)
 * - Includes vendor names for readability
 * - Includes approver names
 * - Shows complete transfer history
 */
export const getDriverTransferHistoryV2 =
	({ driver_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'driver_transfer_history',
				filter_by: {
					driver_id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS)
			.then((response) => {
				// Check if response exists (ACL validation might return undefined)
				if (!response) {
					return {
						driver_id: null,
						driver_name: null,
						current_vendor_id: null,
						current_vendor_name: null,
						transfer_history: [],
						total_transfers: 0,
					};
				}

				if (response?.status === 'success' && response?.data) {
					return {
						driver_id: response.data.driver_id || null,
						driver_name: response.data.driver_name || null,
						current_vendor_id: response.data.current_vendor_id || null,
						current_vendor_name: response.data.current_vendor_name || null,
						transfer_history: response.data.transfer_history || [],
						total_transfers: response.data.total_transfers || 0,
					};
				}

				return {
					driver_id: null,
					driver_name: null,
					current_vendor_id: null,
					current_vendor_name: null,
					transfer_history: [],
					total_transfers: 0,
				};
			})
			.catch((error) => {
				console.error('Get driver transfer history error:', error);
				const errorMessage =
					error?.response?.data?.error?.message ||
					error?.response?.data?.message ||
					error?.message ||
					'Failed to fetch driver transfer history';
				toaster.error(errorMessage);
				return {
					driver_id: null,
					driver_name: null,
					current_vendor_id: null,
					current_vendor_name: null,
					transfer_history: [],
					total_transfers: 0,
				};
			});
	};

export default driverSliceV2.reducer;
